import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useReactive, useUnmount } from 'ahooks';
import { Button, Modal, Popconfirm, Typography } from 'antd';
import { Input, Table, message } from 'antd';
import { isEmpty, map, some, sumBy } from 'lodash';
import { EntitiesParams, EntityType } from 'types/getParams';

import { OrderResp } from '@api/responseModels/order/orderResponse';
import { CRMAPIManager } from '@classes/crmApiManager';
import { messageService } from '@classes/messageService';
import { SettingsManager } from '@classes/settingsManager';
import { QuickAddEntityButtons, SearchTabs } from '@components/common/searchTabs';
import { SearchTab, SelectedTabItem } from '@components/common/searchTabs/common/types';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faKaggle } from '@fortawesome/free-brands-svg-icons';
import {
    faBarcode,
    faHammer,
    faScrewdriverWrench,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@tanstack/react-query';
import { capitalizeFirstLetter } from '@utils/capitalizeFirstLetter';

import { fetchEntitiesList } from '../../../orderProfile/common/api';
import {
    TSearchResultItem,
    useQuickAddEntity,
} from '../../../orderProfile/common/hooks/useQuickAddEntity';
import { RelatedEntitiesList } from '../../../orderProfile/components/relatedEntitiesList/relatedEntitiesList';
import { CustomNumberInput } from './blocks/customNumberInput';

import './style.scss';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    orderData: any;
}

export const FillOrderModal: React.FC<Props> = ({ isOpen, onClose, orderData }) => {
    const creds = SettingsManager.getConnectionCredentials();
    const [searchValue, setSearchValue] = useState('');
    const [timerId, setTimerId] = useState(null);
    const [entitiesData, setEntitiesData] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [isSearchPending, setIsSearchPending] = useState(false);

    const tableRefs = [
        useRef<HTMLDivElement>(null),
        useRef<HTMLDivElement>(null),
        useRef<HTMLDivElement>(null),
        useRef<HTMLDivElement>(null),
    ];
    const isSyncingRef = useRef(false);

    const selectedEntitiesState = useReactive({
        products: [],
        services: [],
        tasks: [],
        combo: [],
    });

    const isEmtySelectedEntities = useMemo(() => {
        return (
            isEmpty(selectedEntitiesState.products) &&
            isEmpty(selectedEntitiesState.services) &&
            isEmpty(selectedEntitiesState.tasks)
        );
    }, [
        JSON.stringify(selectedEntitiesState.products),
        JSON.stringify(selectedEntitiesState.services),
        JSON.stringify(selectedEntitiesState.tasks),
    ]);

    const isSelectedEntitiesOver2 = useMemo(() => {
        const arr = [
            ...selectedEntitiesState.products,
            ...selectedEntitiesState.services,
            ...selectedEntitiesState.tasks,
        ];
        return arr.length > 1;
    }, [
        selectedEntitiesState.products,
        selectedEntitiesState.services,
        selectedEntitiesState.tasks,
    ]);

    const commonEntities = useReactive({
        products: [],
        services: [],
        tasks: [],
        combo: [],
    });

    const navigate = useNavigate();

    const [isKeyboardActive, setIsKeyboardActive] = useState(false);
    const [innerHeight, setInnerHeight] = useState(window.innerHeight);

    const { mutate: mutateEntities, isPending: isEntitiesPending } = useMutation({
        mutationFn: (params: EntitiesParams) => fetchEntitiesList(params),
        onSuccess: (data) => setEntitiesData(data),
        onSettled: () => setIsSearchPending(false),
    });

    function getEntitiesDto(entities) {
        return map(entities, (entity) => ({
            id: entity.id,
            price: entity.price,
            quantity: entity.quantity || 1,
            duration: entity.duration,
        }));
    }

    const forceDeleteOrder = async () => {
        try {
            setIsLoading(true);
            await CRMAPIManager.request<OrderResp>(async (api) => {
                return await api.deleteForceOrder(orderData.id, creds.crmID);
            });
        } catch (errors) {
            messageService.sendErrorList(errors);
        } finally {
            setIsLoading(false);
        }
    };

    const saveOrder = async () => {
        try {
            if (!orderData?.id) {
                message.error('Не удалось сохранить: отсутствует идентификатор заказа');
                return;
            }
            if (!orderData?.customer?.id) {
                message.error('Не удалось сохранить: не выбран клиент заказа');
                return;
            }
            setIsLoading(true);
            await CRMAPIManager.request<OrderResp>(async (api) => {
                const profileId = orderData.customer.id;

                return await api.updateOrder({
                    crmID: creds.crmID,
                    id: orderData.id,
                    products: getEntitiesDto(selectedEntitiesState.products),
                    services: getEntitiesDto(selectedEntitiesState.services),
                    tasks: getEntitiesDto(selectedEntitiesState.tasks),
                    profileId,
                    order_object_id: orderData?.order_object?.id,
                    order_status_id: orderData.order_status?.id,
                    profileType: 'customer',
                    // startPlannedAt: moment()
                    //     .local()
                    //     .add(30, 'minutes')
                    //     .format('YYYY-MM-DD HH:mm:ss'),
                });
            });
        } catch (errors) {
            messageService.sendErrorList(errors);
        } finally {
            setIsLoading(false);
        }
    };

    const getModifiedEntity = (type: 'product' | 'service' | 'task' | 'combo') =>
        entitiesData?.filter((entity) => entity.type === type);

    useEffect(() => {
        if (isEntitiesPending) return;

        commonEntities.products = getModifiedEntity('product');
        commonEntities.services = getModifiedEntity('service');
        commonEntities.tasks = getModifiedEntity('task');
        commonEntities.combo = getModifiedEntity('combo');
    }, [entitiesData, isEntitiesPending]);

    useEffect(() => {
        const initialWindowHeight = window.innerHeight;

        const handleResize = () => {
            const isKeyboardNowActive = initialWindowHeight - window.innerHeight > 100;
            setInnerHeight(window.innerHeight);
            setIsKeyboardActive(isKeyboardNowActive);
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSearch = ({ target: { value: rawValue } }) => {
        const value = capitalizeFirstLetter(rawValue);

        if (timerId) {
            clearTimeout(timerId);
        }
        setSearchValue(value);

        // Поле очистили — крестиком antd или стерев вручную. Запрашивать нечего:
        // вкладки при пустом запросе и так скрыты (`showTabData`), а `term: ''`
        // вернул бы весь справочник
        if (!value) {
            setTimerId(null);
            setEntitiesData([]);
            setIsSearchPending(false);
            return;
        }

        setIsSearchPending(true);

        const timeout = setTimeout(() => {
            const params = {
                crm_id: creds.crmID,
                term: value,
                entityType: ['product', 'service', 'task'] as EntityType[],
            };

            mutateEntities(params);
        }, 500);

        setTimerId(timeout);
    };

    const handleSelectItem = (item: SelectedTabItem, key: string) => {
        const entity = {
            ...item,
        };

        if (some(selectedEntitiesState[key], { id: entity.id })) {
            selectedEntitiesState[key] = selectedEntitiesState[key].filter(
                (elem) => elem.id !== entity.id
            );
        } else {
            selectedEntitiesState[key] = [...selectedEntitiesState[key], entity];
        }
    };

    const { canAddEntity, addingEntityType, handleAddOrSelectEntity } = useQuickAddEntity({
        searchValue,
        isBusy: isEntitiesPending || isSearchPending,
        crmId: creds?.crmID,
        getEntitiesByType: (type) => commonEntities[type] as TSearchResultItem[],
        onSelectExisting: handleSelectItem,
        onEntityCreated: (type, item) => {
            (selectedEntitiesState[type] as SelectedTabItem[]).push(item);
            setSearchValue('');
        },
    });

    const handleRemoveItem = (item: SelectedTabItem, key: string) => {
        selectedEntitiesState[key] = selectedEntitiesState[key].filter(
            (elem) => elem.id !== item.id
        );
    };

    const normalizeKey = (key: string) => (key.endsWith('s') ? key : `${key}s`);

    const handlePriceChange = (item: SelectedTabItem, key: string, value: string) => {
        const targetKey = normalizeKey(key);
        selectedEntitiesState[targetKey] = selectedEntitiesState[targetKey]?.map((elem) => {
            if (elem.id === item.id) {
                elem.price = value;
            }
            return elem;
        });
    };

    const handleQuantityChange = (item: SelectedTabItem, key: string, value: string) => {
        const targetKey = normalizeKey(key);
        selectedEntitiesState[targetKey] = selectedEntitiesState[targetKey]?.map((elem) => {
            if (elem.id === item.id) {
                elem.quantity = value;
            }
            return elem;
        });
    };

    const columns = [
        {
            title: <h3 style={{ fontSize: '12px', color: '#8b4f01' }}>Название</h3>,
            dataIndex: 'name',
            key: 'name',
            width: '50%',
        },
        {
            title: <h3 style={{ fontSize: '12px', color: '#8b4f01' }}>Кол-во</h3>,
            width: '17%',
            key: 'quantity',
            dataIndex: 'quantity',
            render: (val, record) => {
                return (
                    <CustomNumberInput
                        onQuantityChange={handleQuantityChange}
                        record={record}
                        value={val}
                    />
                );
            },
        },
        {
            title: <h3 style={{ fontSize: '12px', color: '#8b4f01' }}>Цена</h3>,
            width: '28%',
            dataIndex: 'price',
            key: 'price',
            render: (val, record) => {
                return (
                    <CustomNumberInput
                        onQuantityChange={handlePriceChange}
                        record={record}
                        value={val}
                    />
                );
            },
        },
        {
            className: 'delete-column-custom',
            title: null,
            width: '5%',
            fixed: 'right',
            key: 'action',
            render: (_, record) => {
                return (
                    <div
                        style={{ textAlign: 'center', width: '100%', cursor: 'pointer' }}
                        onClick={() =>
                            handleRemoveItem(
                                record,
                                record.type[record.type.length - 1] === 's'
                                    ? record.type
                                    : record.type + 's'
                            )
                        }
                    >
                        <FontAwesomeIcon icon={faXmark} color="red" size="sm" />
                    </div>
                );
            },
        },
    ];

    const tabs = useMemo(
        () =>
            [
                {
                    key: 'services',
                    label: null,
                    icon: faScrewdriverWrench,
                    isLoading: isEntitiesPending,
                    data: commonEntities.services,
                },
                {
                    key: 'products',
                    label: null,
                    icon: faBarcode,
                    isLoading: isEntitiesPending,
                    data: commonEntities.products,
                },
                {
                    key: 'tasks',
                    label: null,
                    icon: faHammer,
                    isLoading: isEntitiesPending,
                    data: commonEntities.tasks,
                },
                {
                    key: 'combo',
                    label: '',
                    icon: faKaggle as IconProp,
                    isLoading: isEntitiesPending,
                    data: commonEntities.combo,
                },
            ] as SearchTab[],
        [
            entitiesData,
            isEntitiesPending,
            commonEntities.combo,
            commonEntities.tasks,
            commonEntities.products,
            commonEntities.services,
        ]
    );

    const total = useMemo(
        () =>
            sumBy(
                [
                    ...selectedEntitiesState.products,
                    ...selectedEntitiesState.services,
                    ...selectedEntitiesState.tasks,
                ],
                (item) => item.price * item.quantity
            ),
        [
            JSON.stringify(selectedEntitiesState.products),
            JSON.stringify(selectedEntitiesState.services),
            JSON.stringify(selectedEntitiesState.tasks),
        ]
    );

    useEffect(() => {
        const elements = tableRefs
            ?.map((ref) => ref.current?.querySelector('.ant-table-content'))
            ?.filter(Boolean) as HTMLDivElement[];

        elements.forEach((el, index) => {
            el.addEventListener('scroll', () => {
                if (isSyncingRef.current) return;

                isSyncingRef.current = true;
                const scrollLeft = el.scrollLeft;
                elements.forEach((other, otherIndex) => {
                    if (index !== otherIndex) {
                        other.scrollLeft = scrollLeft;
                    }
                });
                isSyncingRef.current = false;
            });
        });
    }, [tableRefs]);

    useUnmount(() => {
        if (timerId) clearTimeout(timerId);
    });

    return (
        <Modal
            wrapClassName="fill-order-modal"
            centered
            // cancelButtonProps={{}}
            closable={false}
            maskClosable={false}
            title="Заполнение заказа"
            open={isOpen}
            // open
            onCancel={onClose}
            footer={[
                <Popconfirm
                    key="delete"
                    title="Заказ будет удален"
                    onConfirm={async () => {
                        await forceDeleteOrder();
                        onClose();
                    }}
                    okText="Подтвердить"
                    cancelText="Отмена"
                    placement="topLeft"
                    disabled={isLoading}
                >
                    <Button loading={isLoading} type="primary" danger>
                        Удалить заказ
                    </Button>
                </Popconfirm>,
                <Button
                    disabled={isEmtySelectedEntities}
                    key="go-to-order"
                    type="default"
                    loading={isLoading}
                    onClick={async () => {
                        if (!orderData?.id) {
                            message.error('Не удалось открыть заказ: отсутствует идентификатор');
                            return;
                        }
                        await saveOrder();
                        navigate(`/lk/worker/crm/${creds.crmID}/orders/${orderData.id}`);
                    }}
                    style={{
                        backgroundColor: isEmtySelectedEntities ? '#f5f5f5' : '#069806',
                        color: isEmtySelectedEntities ? 'rgba(0, 0, 0, 0.25)' : 'white',
                    }}
                >
                    К заказу
                </Button>,
                <Button
                    loading={isLoading}
                    key="set-order-priority"
                    type="primary"
                    onClick={async () => {
                        await saveOrder();
                        navigate(`/lk/worker/crm/${creds.crmID}/orders/${orderData.id}/workflow`);
                    }}
                    disabled={!isSelectedEntitiesOver2}
                >
                    Задать очередность
                </Button>,
            ]}
            styles={{ body: { padding: '3px' } }}
        >
            <div>
                <QuickAddEntityButtons
                    canAddEntity={canAddEntity}
                    addingEntityType={addingEntityType}
                    onSelect={handleAddOrSelectEntity}
                />
                <div className="search-container-fill-order">
                    <div className="search-wrapper-fill-order">
                        <Input
                            id="search"
                            style={{
                                border: 'solid 1px #ff9100',
                                borderBottom: 'none',
                                borderRadius: '10px 10px 0px 0px',
                            }}
                            onChange={handleSearch}
                            value={searchValue}
                            placeholder="Поиск по названию услуги / товара / задачи"
                            allowClear
                        />
                    </div>
                </div>
                <SearchTabs
                    searchValue={searchValue}
                    showTabData={!!searchValue}
                    style={{
                        maxHeight: isKeyboardActive
                            ? innerHeight - 60 + 'px'
                            : innerHeight - 60 - 72.71 - 15 + 'px',
                        overflow: 'scroll',
                    }}
                    selectedTabItems={{
                        services: selectedEntitiesState.services,
                        products: selectedEntitiesState.products,
                        tasks: selectedEntitiesState.tasks,
                        combo: selectedEntitiesState.combo,
                    }}
                    onTabItemSelect={handleSelectItem}
                    onTabItemQuantityChange={handleQuantityChange}
                    tabs={tabs}
                />
            </div>

            <div>
                <RelatedEntitiesList data={[]} />
                <div style={{ width: '100%', height: '2px', backgroundColor: 'black' }} />
                <div ref={tableRefs[3]}>
                    <Table
                        className="fill-order-header-table"
                        columns={columns}
                        dataSource={[]}
                        pagination={false}
                        scroll={{ x: 450 }}
                        locale={{ emptyText: null }}
                        footer={null}
                    />
                </div>
                <div style={{ width: '100%', height: '2px', backgroundColor: 'black' }} />
                <h3>Услуги:</h3>
                <div ref={tableRefs[0]}>
                    <Table
                        showHeader={false}
                        columns={columns}
                        dataSource={selectedEntitiesState.services}
                        pagination={false}
                        scroll={{ x: 450 }}
                        locale={{ emptyText: null }}
                    />
                </div>

                <div style={{ width: '100%', height: '2px', backgroundColor: 'black' }} />
                <h3>Товары:</h3>
                <div ref={tableRefs[1]}>
                    <Table
                        showHeader={false}
                        columns={columns}
                        dataSource={selectedEntitiesState.products}
                        pagination={false}
                        scroll={{ x: 450 }}
                        locale={{ emptyText: null }}
                    />
                </div>
                <div style={{ width: '100%', height: '2px', backgroundColor: 'black' }} />
                <h3>Задачи:</h3>
                <div ref={tableRefs[2]}>
                    <Table
                        showHeader={false}
                        columns={columns}
                        dataSource={selectedEntitiesState.tasks}
                        pagination={false}
                        scroll={{ x: 450 }}
                        locale={{ emptyText: null }}
                    />
                </div>

                <Typography.Title style={{ textAlign: 'center' }} level={5}>
                    Итого {total?.toLocaleString('ru-RU')} ₽
                </Typography.Title>
            </div>
        </Modal>
    );
};
